import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import * as THREE from "three";
import { Html } from "@react-three/drei";
import type { ThreeEvent } from "@react-three/fiber";
import type { CenterlineData } from "./useTunnelCenterline";
import { useProject } from "./ProjectContext";
import {
  type ProfileAssignment,
  getProfileAtStation,
} from "./tunnelProfileShapes";

interface MWDRound {
  stationStart: number;
  stationEnd: number;
  rockType: string;
  penetrationRate: number;
  rotationPressure: number;
  feedPressure: number;
  hammerPressure: number;
}

interface MWDData {
  tunnel: string;
  pegOffset: number;
  rounds: MWDRound[];
}

/** Rock type → color mapping */
const ROCK_COLORS: Record<string, string> = {
  Dark_shale: "#303030",
  Gray_shale: "#807060",
  Mixed_sediment: "#b0a080",
  Limestone: "#c0b080",
  Volcanic_rock: "#707090",
  Rhomb_porphyry: "#e06040",
  Basalt: "#404080",
  Diabase: "#306030",
  Drammensgranite: "#d0a0a0",
  Granite: "#c0a0a0",
  Hornfels: "#606060",
  Syenite: "#b08080",
  Pegmatite: "#e0d0e0",
  Breccia: "#908060",
  Gravel: "#c0b090",
};

const DEFAULT_COLOR = "#888888";

/** Sampling step along the centerline (meters) */
const STEP = 1;

/** Hover tooltip info */
interface HoverInfo {
  rockType: string;
  color: string;
  station: number;
  point: THREE.Vector3;
}

/**
 * Look up the MWD round at a given station.
 */
function getRoundAtStation(rounds: MWDRound[], station: number): MWDRound | null {
  for (const r of rounds) {
    if (station >= r.stationStart && station < r.stationEnd) return r;
  }
  return null;
}

/**
 * Renders MWD rock type data as colored surfaces on the tunnel profile mesh.
 * Each meter of tunnel is colored by the predicted rock type at that station.
 * Hovering shows a tooltip with rock type and station.
 */
export function MWDRockType3D({ data, visible, profileAssignments, opacity = 1 }: {
  data: CenterlineData;
  visible: boolean;
  profileAssignments: ProfileAssignment[];
  opacity?: number;
}) {
  const project = useProject();
  const [mwd, setMwd] = useState<MWDData | null>(null);
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!project.mwdEndpoint) return;
    setMwd(null);
    fetch(project.mwdEndpoint)
      .then((r) => r.json())
      .then((d: MWDData) => setMwd(d))
      .catch((e) => console.error("Failed to load MWD data:", e));
  }, [project.mwdEndpoint]);

  // Build geometry + face-to-station lookup
  const { geometry, faceStations } = useMemo(() => {
    if (!mwd || !visible) return { geometry: null, faceStations: null };

    const totalLength = data.totalLength;
    const worldUp = new THREE.Vector3(0, 1, 0);
    const rounds = mwd.rounds;

    // Sample stations at STEP intervals
    const stations: number[] = [];
    for (let s = 0; s <= totalLength; s += STEP) {
      stations.push(s);
    }
    if (stations[stations.length - 1] < totalLength) {
      stations.push(totalLength);
    }

    interface RingInfo {
      verts: THREE.Vector3[];
      color: THREE.Color | null;
      station: number;
    }

    const rings: RingInfo[] = [];

    for (const station of stations) {
      const t = Math.max(0, Math.min(1, station / totalLength));
      const point = data.curve.getPointAt(t);
      const tangent = data.curve.getTangentAt(t).normalize();

      let side = new THREE.Vector3().crossVectors(tangent, worldUp).normalize();
      if (side.lengthSq() < 0.001) {
        side.crossVectors(tangent, new THREE.Vector3(1, 0, 0)).normalize();
      }
      const up = new THREE.Vector3().crossVectors(side, tangent).normalize();

      const profile = getProfileAtStation(station, profileAssignments);
      const verts = profile.points.map((p2d) =>
        new THREE.Vector3(
          point.x + p2d.x * side.x + p2d.y * up.x,
          point.y + p2d.x * side.y + p2d.y * up.y,
          point.z + p2d.x * side.z + p2d.y * up.z,
        )
      );

      const round = getRoundAtStation(rounds, station);
      const color = round ? new THREE.Color(ROCK_COLORS[round.rockType] ?? DEFAULT_COLOR) : null;
      rings.push({ verts, color, station });
    }

    // Build mesh
    const allPositions: number[] = [];
    const allColors: number[] = [];
    const allIndices: number[] = [];
    // Map: face index (triangle index) → station of the ring pair
    const faceStationMap: number[] = [];
    let vertexOffset = 0;

    for (let i = 0; i < rings.length - 1; i++) {
      const rA = rings[i];
      const rB = rings[i + 1];
      const color = rA.color ?? rB.color;
      if (!color) continue;

      const vertCount = Math.min(rA.verts.length, rB.verts.length);
      if (vertCount < 2) continue;

      const base = vertexOffset;

      for (let j = 0; j < vertCount; j++) {
        const v = rA.verts[j];
        allPositions.push(v.x, v.y, v.z);
        allColors.push(color.r, color.g, color.b);
      }
      for (let j = 0; j < vertCount; j++) {
        const v = rB.verts[j];
        allPositions.push(v.x, v.y, v.z);
        allColors.push(color.r, color.g, color.b);
      }

      for (let j = 0; j < vertCount - 1; j++) {
        const a = base + j;
        const b = base + j + 1;
        const c = base + vertCount + j;
        const d = base + vertCount + j + 1;
        allIndices.push(a, c, b);
        faceStationMap.push(rA.station);
        allIndices.push(b, c, d);
        faceStationMap.push(rA.station);
      }

      vertexOffset += vertCount * 2;
    }

    if (allPositions.length === 0) return { geometry: null, faceStations: null };

    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.Float32BufferAttribute(allPositions, 3));
    geom.setAttribute("color", new THREE.Float32BufferAttribute(allColors, 3));
    geom.setIndex(allIndices);
    geom.computeVertexNormals();
    return { geometry: geom, faceStations: faceStationMap };
  }, [mwd, data, visible, profileAssignments]);

  const onPointerMove = useCallback((e: ThreeEvent<PointerEvent>) => {
    if (!mwd || !faceStations) return;
    e.stopPropagation();
    const faceIdx = e.faceIndex;
    if (faceIdx == null || faceIdx >= faceStations.length) {
      setHover(null);
      return;
    }
    const station = faceStations[faceIdx];
    const round = getRoundAtStation(mwd.rounds, station);
    if (!round) { setHover(null); return; }

    // Debounce state updates
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setHover({
        rockType: round.rockType,
        color: ROCK_COLORS[round.rockType] ?? DEFAULT_COLOR,
        station: Math.round(station),
        point: e.point.clone(),
      });
    }, 16);
  }, [mwd, faceStations]);

  const onPointerLeave = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    // Keep tooltip visible briefly so it survives screenshot/focus loss
    hoverTimeout.current = setTimeout(() => setHover(null), 2000);
  }, []);

  if (!visible || !geometry) return null;

  return (
    <group>
      <mesh
        geometry={geometry}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
      >
        <meshLambertMaterial vertexColors side={THREE.DoubleSide} transparent={opacity < 1} opacity={opacity} />
      </mesh>
      {hover && (
        <Html
          position={[hover.point.x, hover.point.y, hover.point.z]}
          center
          style={{ pointerEvents: "none" }}
        >
          <div style={{
            background: "rgba(0,0,0,0.82)",
            backdropFilter: "blur(8px)",
            color: "#f0f0f0",
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 12,
            fontFamily: "system-ui, sans-serif",
            fontWeight: 500,
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: 7,
            transform: "translateY(-28px)",
            border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
          }}>
            <span style={{
              width: 10,
              height: 10,
              borderRadius: 3,
              background: hover.color,
              flexShrink: 0,
            }} />
            <span>{hover.rockType.replace(/_/g, " ")}</span>
            <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
              {hover.station}m
            </span>
          </div>
        </Html>
      )}
    </group>
  );
}

/** Returns the rock type color legend for use in the sidebar */
export function getMWDRockTypes(mwdData: MWDData | null): { rockType: string; color: string }[] {
  if (!mwdData) return [];
  const types = [...new Set(mwdData.rounds.map((r) => r.rockType))];
  return types.map((t) => ({ rockType: t, color: ROCK_COLORS[t] ?? DEFAULT_COLOR }));
}

export type { MWDData, MWDRound };
